import React, { useEffect, useState } from 'react';
import { errorToast, successToast } from '../../utils/helper';
import Loader from '../../component/common/Loader';
import SearchInput from '../../component/common/Input/SearchInput';
import ToggleSwitch from '../../component/common/ToggleSwitch';
import { Api } from '../../api';

const AddUsersModal = ({ isOpen, onClose, onSuccess }) => {
  const [loader, setLoader] = useState(false);
  const [companies, setCompanies] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [users, setUsers] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [addedUsers, setAddedUsers] = useState([]); // Users to be added (havePlan: false -> true)
  const [removedUsers, setRemovedUsers] = useState([]); // Users to be removed (havePlan: true -> false)
  const [searchTerm, setSearchTerm] = useState('');
  const [schemes, setSchemes] = useState([]);
  const [selectedScheme, setSelectedScheme] = useState(null);

  // Company dropdown states
  const [companyDropdownOpen, setCompanyDropdownOpen] = useState(false);
  const [companySearchText, setCompanySearchText] = useState('');
  const [displayText, setDisplayText] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [isSearching, setIsSearching] = useState(false);

  useEffect(() => {
    if (isOpen) {
      fetchCompaniesWithSchemes(1, '');
    }
  }, [isOpen]);

  useEffect(() => {
    if (selectedCompany) {
      fetchUnenrolledUsers();
      // Filter schemes for the selected company
      const companySchemes = schemes.filter(
        (scheme) => scheme.companyId === selectedCompany.companyId
      );
      if (companySchemes.length > 0) {
        setSelectedScheme(companySchemes[0]); // Auto-select first scheme
      } else {
        setSelectedScheme(null);
      }
    } else {
      setUsers([]);
      setSelectedUsers([]);
      setAddedUsers([]);
      setRemovedUsers([]);
      setSelectedScheme(null);
    }
  }, [selectedCompany]);

  // Debounce user search
  useEffect(() => {
    if (searchTerm !== undefined && selectedCompany) {
      const delayDebounceFn = setTimeout(() => {
        fetchUnenrolledUsers();
      }, 800);
      return () => clearTimeout(delayDebounceFn);
    }
  }, [searchTerm]);

  // Debounce company search
  useEffect(() => {
    if (companySearchText !== undefined && companyDropdownOpen) {
      const delayDebounceFn = setTimeout(() => {
        setIsSearching(true);
        setCurrentPage(1);
        setCompanies([]);
        fetchCompaniesWithSchemes(1, companySearchText);
      }, 800);
      return () => clearTimeout(delayDebounceFn);
    }
  }, [companySearchText]);

  const fetchCompaniesWithSchemes = async (page = 1, search = '') => {
    const loadingState = page === 1 ? setLoader : setIsLoadingMore;
    loadingState(true);

    try {
      const response = await Api.getCompaniesWithHealthScheme(page, 10, search, true);

      if (response?.data?.meta?.code === 1) {
        const newCompanies = response?.data?.data || [];
        const totalRecords = response?.data?.totalRecords || 0;

        // Transform to company objects
        const companyList = newCompanies.map((company) => ({
          companyId: company.companyId,
          companyName: company.companyName,
          schemeId: company.schemeId,
          providerName: company.providerName || 'Shoorah'
        }));

        if (page === 1) {
          setCompanies(companyList);
        } else {
          setCompanies((prev) => [...prev, ...companyList]);
        }

        // Store schemes for later use
        const schemesList = newCompanies.map((company) => ({
          schemeId: company.schemeId,
          companyId: company.companyId,
          schemeName: company.providerName || 'Shoorah'
        }));

        if (page === 1) {
          setSchemes(schemesList);
        } else {
          setSchemes((prev) => [...prev, ...schemesList]);
        }

        setHasMore(companies.length + companyList.length < totalRecords);
        setCurrentPage(page);
      } else {
        errorToast(response?.data?.message || 'Failed to fetch companies');
        if (page === 1) {
          setCompanies([]);
          setSchemes([]);
        }
      }
    } catch (error) {
      console.error('Error fetching companies:', error);
      errorToast('Failed to fetch companies');
      if (page === 1) {
        setCompanies([]);
        setSchemes([]);
      }
    } finally {
      loadingState(false);
      setIsSearching(false);
    }
  };

  const fetchUnenrolledUsers = async () => {
    if (!selectedCompany) return;

    setLoader(true);
    try {
      const response = await Api.getHealthSchemeUnenrolledUsers(
        selectedCompany.companyId,
        searchTerm
      );

      if (response?.data?.meta?.code === 1) {
        setUsers(response?.data?.data || []);
      } else {
        errorToast(response?.data?.message || 'Failed to fetch users');
        setUsers([]);
      }
    } catch (error) {
      console.error('Error fetching users:', error);
      errorToast('Failed to fetch users');
      setUsers([]);
    } finally {
      setLoader(false);
    }
  };

  const handleCompanySelect = (company) => {
    setSelectedCompany(company);
    setDisplayText(company.companyName);
    setCompanyDropdownOpen(false);
    setCompanySearchText('');
  };

  const handleSearchFocus = () => {
    setCompanyDropdownOpen(true);
    if (companies.length === 0) {
      fetchCompaniesWithSchemes(1, '');
    }
  };

  const handleDropdownScroll = (e) => {
    const { scrollTop, scrollHeight, clientHeight } = e.target;
    if (scrollHeight - scrollTop <= clientHeight * 1.5 && hasMore && !isLoadingMore) {
      fetchCompaniesWithSchemes(currentPage + 1, companySearchText);
    }
  };

  const handleUserToggle = (user) => {
    const userId = user.userId || user._id;

    if (user.havePlan) {
      // User already has plan - toggling will remove them
      setRemovedUsers((prev) => {
        if (prev.includes(userId)) {
          // Already in removed list, toggle back (remove from removed list)
          return prev.filter((id) => id !== userId);
        } else {
          // Add to removed list
          return [...prev, userId];
        }
      });

      // Remove from added list if present
      setAddedUsers((prev) => prev.filter((id) => id !== userId));
    } else {
      // User doesn't have plan - toggling will add them
      setAddedUsers((prev) => {
        if (prev.includes(userId)) {
          // Already in added list, toggle back (remove from added list)
          return prev.filter((id) => id !== userId);
        } else {
          // Add to added list
          return [...prev, userId];
        }
      });

      // Remove from removed list if present
      setRemovedUsers((prev) => prev.filter((id) => id !== userId));
    }

    // Update selectedUsers for UI state
    setSelectedUsers((prev) => {
      if (prev.includes(userId)) {
        return prev.filter((id) => id !== userId);
      } else {
        return [...prev, userId];
      }
    });
  };

  const handleSelectAll = () => {
    if (selectedUsers.length === users.length) {
      // Deselect all
      setSelectedUsers([]);
      setAddedUsers([]);
      users.forEach((user) => {
        const userId = user.userId || user._id;
        // If user had plan, add to removedUsers
        if (user.havePlan) {
          setRemovedUsers((prev) => [...prev, userId]);
        }
      });
    } else {
      // Select all
      const allUserIds = users.map((user) => user.userId || user._id);
      setSelectedUsers(allUserIds);

      // Separate users into added and removed based on havePlan
      const usersToAdd = [];
      const usersToRemove = [];

      users.forEach((user) => {
        const userId = user.userId || user._id;

        usersToAdd.push(userId);
      });

      setAddedUsers(usersToAdd);
      setRemovedUsers(usersToRemove);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!selectedCompany) {
      errorToast('Please select a company');
      return;
    }

    if (!selectedScheme) {
      errorToast('No active scheme found for this company');
      return;
    }

    if (addedUsers.length === 0 && removedUsers.length === 0) {
      errorToast('Please make at least one change');
      return;
    }

    setLoader(true);
    try {
      const reqBody = {
        schemeId: selectedScheme.schemeId,
        userIds: addedUsers.map((userId) => userId.toString()),
        removedUserIds: removedUsers.map((userId) => userId.toString())
      };

      const response = await Api.enrollUsersInHealthScheme(reqBody);

      if (response?.data?.meta?.code === 1) {
        const enrolled = addedUsers.length;
        const removed = removedUsers.length;
        let message = '';

        if (enrolled > 0 && removed > 0) {
          message = `Successfully enrolled ${enrolled} user(s) and removed ${removed} user(s)`;
        } else if (enrolled > 0) {
          message = `Successfully enrolled ${enrolled} user(s)`;
        } else if (removed > 0) {
          message = `Successfully removed ${removed} user(s)`;
        }

        successToast(message);
        onSuccess();
      } else {
        errorToast(response?.data?.message || 'Failed to update users');
      }
    } catch (error) {
      console.error('Error updating users:', error);
      errorToast('Failed to update users');
    } finally {
      setLoader(false);
    }
  };

  const handleClose = () => {
    setSelectedCompany(null);
    setUsers([]);
    setSelectedUsers([]);
    setAddedUsers([]);
    setRemovedUsers([]);
    setSearchTerm('');
    setSelectedScheme(null);
    setCompanies([]);
    setCompanyDropdownOpen(false);
    setCompanySearchText('');
    setDisplayText('');
    setCurrentPage(1);
    setHasMore(true);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {loader && <Loader />}
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black bg-opacity-50" onClick={handleClose}></div>

      {/* Modal */}
      <div className="relative bg-white dark:bg-shoorah-darkBgTabColor rounded-lg shadow-xl w-full max-w-2xl mx-4 p-6 max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-semibold text-shoorah-primary dark:text-white">
            Add Users to Health Scheme
          </h2>
          <button
            onClick={handleClose}
            className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-6 w-6"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="space-y-4">
            {/* Company Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Company <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <input
                  type="text"
                  className="w-full rounded-lg px-4 py-2 bg-shoorah-normanGray border-[1.5px] border-shoorah-borderGray dark:bg-shoorah-darkBgColor dark:border-shoorah-darkBorder focus:outline-none focus:border-shoorah-primary"
                  placeholder="Search for a company..."
                  value={companyDropdownOpen ? companySearchText : displayText}
                  onChange={(e) => setCompanySearchText(e.target.value)}
                  onFocus={handleSearchFocus}
                />

                {companyDropdownOpen && (
                  <div className="absolute z-10 w-full mt-1 bg-white dark:bg-shoorah-darkBgColor border border-gray-300 dark:border-gray-600 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                    {isSearching ? (
                      <div className="px-4 py-3 text-center text-gray-500 dark:text-gray-400">
                        Searching...
                      </div>
                    ) : companies.length > 0 ? (
                      <div onScroll={handleDropdownScroll}>
                        {companies.map((company) => (
                          <div
                            key={company.companyId}
                            onClick={() => handleCompanySelect(company)}
                            className="px-4 py-2 hover:bg-gray-100 dark:hover:bg-gray-700 cursor-pointer text-gray-900 dark:text-white"
                          >
                            {company.companyName}
                          </div>
                        ))}
                        {isLoadingMore && (
                          <div className="px-4 py-2 text-center text-gray-500 dark:text-gray-400">
                            Loading more...
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="px-4 py-3 text-center text-gray-500 dark:text-gray-400">
                        No companies found
                      </div>
                    )}
                  </div>
                )}

                {companyDropdownOpen && (
                  <div
                    className="fixed inset-0 z-[5]"
                    onClick={() => setCompanyDropdownOpen(false)}
                  ></div>
                )}
              </div>
            </div>

            {/* Scheme Info */}

            {/* User Search */}
            {selectedCompany && (
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Search Users
                </label>
                <SearchInput
                  id="searchUsers"
                  name="searchUsers"
                  type="text"
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Search by name or email..."
                />
              </div>
            )}

            {/* Users List */}
            {selectedCompany && users.length > 0 && (
              <div>
                <div className="flex justify-between items-center mb-2">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Select Users <span className="text-red-500">*</span>
                    <span className="text-gray-500 font-normal ml-2">
                      ({selectedUsers.length} selected)
                    </span>
                  </label>
                  <button
                    type="button"
                    onClick={handleSelectAll}
                    className="text-sm text-shoorah-primary hover:text-shoorah-secondary"
                  >
                    {selectedUsers.length === users.length ? 'Deselect All' : 'Select All'}
                  </button>
                </div>

                <div className="border border-gray-300 dark:border-gray-600 rounded-lg max-h-64 overflow-y-auto">
                  {users.map((user) => {
                    const userId = user.userId || user._id;
                    // Determine the toggle state based on original havePlan and user actions
                    let isToggled = user.havePlan;

                    if (user.havePlan) {
                      // If user had plan, check if they're in removed list
                      isToggled = !removedUsers.includes(userId);
                    } else {
                      // If user didn't have plan, check if they're in added list
                      isToggled = addedUsers.includes(userId);
                    }

                    return (
                      <div
                        key={userId}
                        className="flex items-center justify-between p-3 hover:bg-gray-50 dark:hover:bg-gray-700 border-b border-gray-200 dark:border-shoorah-darkBorder last:border-b-0"
                      >
                        <div className="flex-1">
                          <p className="text-sm font-medium text-gray-900 dark:text-white">
                            {user.name}
                          </p>
                          <p className="text-sm text-gray-500 dark:text-gray-400">{user.email}</p>
                        </div>
                        <ToggleSwitch
                          toggleValue={isToggled}
                          setToggleValue={() => handleUserToggle(user)}
                          disabled={false}
                        />
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* No Users Message */}
            {selectedCompany && users.length === 0 && !loader && (
              <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                <p>No unenrolled users found for this company.</p>
                <p className="text-sm mt-1">All users are already enrolled in a scheme.</p>
              </div>
            )}

            {/* No Company Selected */}
            {!selectedCompany && (
              <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                <p>Please select a company to view available users.</p>
              </div>
            )}
          </div>

          {/* Action Buttons */}
          <div className="flex gap-3 mt-6">
            <button
              type="button"
              onClick={handleClose}
              className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-full text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={
                !selectedCompany ||
                (addedUsers.length === 0 && removedUsers.length === 0) ||
                !selectedScheme
              }
              className="btn-primary flex-1"
            >
              Submit
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddUsersModal;
